import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface DatabaseStats {
  speisenCount: number;
  getraenkeCount: number;
  totalFoods: number; // (speisen + getraenke) * 2
  formattedTotalFoods: string; // z.B. "7.400+"
  recognitionRate: number; // Erkennungsrate in %
}

// Statische Fallback-Werte für sofortige Anzeige
const FALLBACK_STATS: DatabaseStats = {
  speisenCount: 3700,
  getraenkeCount: 3700,
  totalFoods: 7400,
  formattedTotalFoods: "7.400+",
  recognitionRate: 94,
};

export function useDatabaseStats() {
  return useQuery({
    queryKey: ["database-stats"],
    queryFn: async (): Promise<DatabaseStats> => {
      // Parallel beide Counts abrufen
      const [speisenResult, getraenkeResult] = await Promise.all([
        supabase.from("speisen").select("id", { count: "exact", head: true }),
        supabase.from("getraenke").select("id", { count: "exact", head: true }),
      ]);

      const speisenCount = speisenResult.count || 0;
      const getraenkeCount = getraenkeResult.count || 0;
      
      // Verdoppelte Anzahl als "Lebensmittel in Datenbank"
      const totalFoods = (speisenCount + getraenkeCount) * 2;
      
      // Formatierung: auf Hunderter abrunden und mit Tausender-Punkt
      const roundedTotal = Math.floor(totalFoods / 100) * 100;
      const formattedTotalFoods = roundedTotal.toLocaleString("de-DE") + "+";

      return {
        speisenCount,
        getraenkeCount,
        totalFoods,
        formattedTotalFoods,
        recognitionRate: 94,
      };
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 Stunden cachen
    gcTime: 1000 * 60 * 60 * 24 * 7, // 7 Tage im Cache behalten
    placeholderData: FALLBACK_STATS, // Sofortige Anzeige mit Fallback
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}
